import React, { useState, useMemo, useEffect, useRef } from "react";
import {
	Avatar,
	Button,
	FormHelperText,
	Grid,
	TextField,
	Typography,
} from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { LoginLogo, attachmentLinkPrimaryIcon } from "assets/images";
import Footer from "components/Layout/Footer";
import CustomTextField from "components/CustomTextField";
import { Loading } from "components/Loading";
import { errorIcon } from "assets/images";
import { BrandingLogo } from "components/BrandingLogo";
import { Shared } from "utility/shared";
import { useParams } from "react-router-dom";
import environment from "environment";
import { VendorBidQueryService } from "services/vendorBidQuery.service";

interface BidQueryConfirmationMasterProps {
	defaultMessageText: string;
	cancelledMessageText: string;
	successMessageText: string;
	cancelledMessageDialogText: string;
	successMessageDialogText: string;
	dialogTitle: string;
	cancelConfirmationDialogTitle: string;
	textFieldLabel?: string;
	errorMessage?: string;
	children?: React.ReactNode;
	declineDialogTitle: string;
}

export enum BidQueryConfirmationType {
	Respond = "respond",
}

const BidQueryConfirmationMaster: React.FC<BidQueryConfirmationMasterProps> = ({
	defaultMessageText,
	cancelledMessageText,
	successMessageText,
	cancelledMessageDialogText,
	successMessageDialogText,
	dialogTitle,
	cancelConfirmationDialogTitle,
	textFieldLabel = "Response",
	errorMessage = "",
	children,
	declineDialogTitle,
}) => {
	const params = useParams();
	const validationSchema = Yup.object().shape({
		response: Yup.string().nullable().required("Please enter response"),
		// .max(250, "Response must be at most 250 characters"),
	});

	class Model {
		vendorBidQueryId: number = 0;
		response: string = "";
	}
	const methods = useForm<Model>({
		defaultValues: new Model(),
		mode: "onChange",
		resolver: yupResolver(validationSchema),
	});

	const responseValue = methods.watch("response");
	const [query, setQuery] = useState("");
	const [selectedVendorBidQueryId, setSelectedVendorBidQueryId] = useState<
		number | null
	>(null);

	const fileUpload = useRef<HTMLInputElement | null>(null);
	const [isActionCompleted, setIsActionCompleted] = useState<boolean>(false);
	const [cancelActionConfirmation, setCancelActionConfirmation] =
		useState<boolean>(false);
	const [isActionCancelled, setIsActionCancelled] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorFileMessage, setErrorFileMessage] = React.useState<string>("");
	const [fileToBeUploaded, setFileToBeUploaded] = React.useState<File | null>(
		null
	);
	const [actionCompleteSuccess, setActionCompleteSuccess] =
		useState<boolean>(false);
	const maxFileSize = environment.documentAttachmentSize;
	const [declineActionConfirmation, setDeclineActionConfirmation] =
		useState<boolean>(false);
	const [isActionDeclined, setIsActionDeclined] = useState<boolean>(false);
	const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
	const [fileErrorMessage, setFileErrorMessage] = useState<boolean>(false);

	useEffect(() => {
		if (actionCompleteSuccess) {
			setIsActionCompleted(true);
		}
		setIsLoading(false);
		return () => {
			setIsLoading(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actionCompleteSuccess]);

	const handleCancelAction = () => {
		// setIsActionCompleted(false);
		// setIsActionCancelled(true);
		// setCancelActionConfirmation(true);
		methods.reset();
		methods.clearErrors();
	};

	const messageText = useMemo(() => {
		if (isActionCompleted) {
			return successMessageText;
		}
		if (isActionCancelled) {
			return cancelledMessageText;
		}
		if (declineActionConfirmation) {
			return successMessageText;
		}
		return defaultMessageText;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		declineActionConfirmation,
		cancelledMessageText,
		defaultMessageText,
		isActionCompleted,
		cancelActionConfirmation,
		isActionCancelled,
		successMessageText,
		isActionDeclined,
	]);

	useEffect(() => {
		const getDecryptedVendorBidQueryId = async () => {
			try {
				const response = await VendorBidQueryService.getVendorBidQueryDetail(
					params.vendorBidQueryId || ""
				);
				setQuery(response.query);
				setSelectedVendorBidQueryId(response.vendorBidQueryId);
			} catch (error: any) {
				setApiErrorMessage(error.badRequestError);
			} finally {
			}
		};
		if (params.vendorBidQueryId) {
			getDecryptedVendorBidQueryId();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params.vendorBidQueryId]);

	const resetFileInputValue = () => {
		// reset input file ref value
		if (fileUpload && fileUpload.current) {
			fileUpload.current.value = "";
		}
	};

	const onFileSelect = (event: any) => {
		setErrorFileMessage("");
		setFileErrorMessage(false);
		const file = event.target.files[0];
		if (file) {
			const isFileSizeValid = file.size / 1024 / 1024 < maxFileSize;
			const isFileNameSizeValid = Shared.isFileNameSizeValid(file.name);
			if (isFileSizeValid && isFileNameSizeValid) {
				setFileToBeUploaded(file);
				// respondQuery(file);
			} else {
				setErrorFileMessage(
					!isFileNameSizeValid
						? "The file name is too long. Rename the file and upload again!"
						: `The file size must be less than ${maxFileSize}MB`
				);
				setFileToBeUploaded(null);
			}
		}
		resetFileInputValue();
	};

	const respondQuery = async () => {
		methods.trigger();
		if (selectedVendorBidQueryId) {
			Shared.showLoader(true);
			const formData: FormData = new FormData();
			formData.append("vendorBidQueryId", selectedVendorBidQueryId.toString());
			formData.append("response", responseValue);
			// if (!fileToBeUploaded) {
			// 	setFileErrorMessage(true);
			// 	return;
			// }
			if (fileToBeUploaded) {
				formData.append("file", fileToBeUploaded);
				formData.append("documentDescription", "");
				formData.append("documentFilePath", fileToBeUploaded.name);
			}
			if (responseValue) {
				try {
					Shared.showLoader(true);
					await VendorBidQueryService.respondVendorBidQuery(formData);
					Shared.showSnackbarMessage("Query Response successful!", "success");
					setActionCompleteSuccess(true);
					setIsActionDeclined(false);
				} catch (error: any) {
					Shared.showSnackbarMessage(
						error.badRequestError || "Error while responding query"
					);
				} finally {
					Shared.showLoader(false);
				}
			}
		}
	};

	return (
		<>
			{isLoading && <Loading />}
			<div className="login-wrapper">
				<div className="login-left">
					<div className="login-left-content">
						<Typography variant="h1">Welcome</Typography>
						<Typography variant="h4" sx={{ fontSize: 24, lineHeight: "30px" }}>
							{!apiErrorMessage
								? errorMessage
									? `${errorMessage} To close this page, please click the close(x) button on top of the page.`
									: messageText
								: apiErrorMessage}
						</Typography>
					</div>
				</div>
				<div className="login-right">
					<Typography variant="body1" className="logo text-center">
						<BrandingLogo className="logo-img" />
					</Typography>
					<div className="login-right-content">
						{errorMessage && (
							<Grid
								container
								padding={3}
								sx={{
									border: "1px solid #034f8b",
									borderRadius: 4,
									marginLeft: 0,
								}}
							>
								<Grid
									item
									xs={12}
									textAlign="center"
									sx={{ paddingBottom: "16px" }}
								>
									<Avatar
										src={errorIcon}
										alt="Error"
										sx={{ margin: "0 auto" }}
										variant="square"
									/>
								</Grid>
								<Grid item xs={12} textAlign="center">
									<Typography
										variant="h3"
										sx={{
											fontSize: "23px !important",
											paddingBottom: "0px !important",
										}}
									>
										{errorMessage}
									</Typography>
								</Grid>
							</Grid>
						)}
						{!errorMessage && !apiErrorMessage && (
							<Grid
								container
								padding={3}
								sx={{
									border: "1px solid #034f8b",
									borderRadius: 4,
									marginLeft: 0,
								}}
							>
								<Grid item xs={12} textAlign="center" sx={{ pb: 2 }}>
									<Typography
										variant="h3"
										sx={{
											color: "#034f8b !important",
											fontSize: "23px !important",
										}}
									>
										{!apiErrorMessage
											? cancelActionConfirmation
												? cancelConfirmationDialogTitle
												: declineActionConfirmation || isActionDeclined
												? declineDialogTitle
												: dialogTitle
											: "Unavailable Action Alert"}
									</Typography>
								</Grid>
								{!isActionCompleted &&
									!cancelActionConfirmation &&
									!isActionCancelled &&
									!declineActionConfirmation &&
									!isActionDeclined &&
									!apiErrorMessage && (
										<FormProvider {...methods}>
											{children}
											<Grid spacing={1} container>
												<Grid item xs={12}>
													<TextField
														label={"Query"}
														multiline
														rows={4}
														value={query}
														disabled
													/>
												</Grid>
												<br />
												<Grid item xs={12}>
													<CustomTextField
														label={textFieldLabel}
														name="response"
														rows={4}
													/>
												</Grid>
												<br />
												<Grid item xs={12}>
													<label
														htmlFor="contained-button-file-1"
														className="upload-signed"
													>
														<input
															id="contained-button-file-1"
															type="file"
															ref={fileUpload}
															onChange={(event: any) => onFileSelect(event)}
														/>
														<Button
															variant="text"
															component="span"
															startIcon={
																<Avatar
																	src={attachmentLinkPrimaryIcon}
																	alt="Attach"
																/>
															}
														>
															Attachment
														</Button>
													</label>
												</Grid>

												{fileErrorMessage && (
													<Grid item xs={12}>
														<FormHelperText error>
															{"Please select file"}
														</FormHelperText>
													</Grid>
												)}

												<Grid item xs={4}>
													<Button
														variant="contained"
														style={{ height: "100%" }}
														onClick={respondQuery}
													>
														Respond
													</Button>
												</Grid>
												<Grid item xs={4}>
													<Button
														variant="contained"
														style={{ height: "100%" }}
														onClick={handleCancelAction}
													>
														Cancel
													</Button>
												</Grid>
											</Grid>
										</FormProvider>
									)}
								{isActionCompleted && !apiErrorMessage && (
									<Grid item xs={12}>
										<Typography variant="h4" className="text-black">
											{successMessageDialogText}
										</Typography>
									</Grid>
								)}
								{apiErrorMessage && (
									<Grid item xs={12} textAlign="center">
										<Typography variant="h4" className="text-black">
											{apiErrorMessage}
										</Typography>
									</Grid>
								)}
								{isActionCancelled && (
									<Grid item xs={12} textAlign="center">
										<Typography variant="h4" className="text-black">
											{cancelledMessageDialogText}
										</Typography>
									</Grid>
								)}
							</Grid>
						)}
					</div>
				</div>
			</div>
			<Footer logoUrl={LoginLogo} />
		</>
	);
};

export default BidQueryConfirmationMaster;
